/* eslint-disable @typescript-eslint/no-explicit-any */
import Confetti from 'react-confetti';
import {
  Badge,
  Box,
  Button,
  Icon,
  Input,
  Label,
  ProgressBar,
  Stack,
  Text
} from '@qcx/ui';
import { Fragment, useState } from 'react';
import { ODIN_PLATFORM_URL } from '../../../../../lib/constants/url.lib';
import {
  faArrowLeft,
  faArrowRight,
  faChevronRight,
  faSync
} from '@fortawesome/pro-light-svg-icons';
import { faCaretRight } from '@fortawesome/pro-solid-svg-icons';
import { route } from '../../../../../route';

interface ShareProps {
  url: string;
  title: string;
  text?: string;
}

interface QuestionProps {
  id: string;
  discipline_name: string;
  subjects: string[];
  associated_text: string | null;
  statement: string;
  alternatives: { item: string; statement: string }[];
  right_answer: string;
}

interface CourseProps {
  course_slug: string;
  opportunity_job_name: string;
  opportunity_job_alias: string | null;
  institute_name: string;
  institute_initials: string;
  institute_image: string | null;
  contest_year: number;
  salary: number;
}

const getCookie = (name: string) => {
  const match = document?.cookie?.match?.(new RegExp(`(^| )${name}=([^;]+)`));

  if (match) {
    return match[2];
  }

  return null;
};

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL'
});

const submit = async (payload: any, callback: (value: any) => void) => {
  try {
    const data = await fetch(route('api-questions'), {
      method: 'POST',
      body: JSON.stringify(payload)
    });

    const response = await data.json();

    callback?.(response);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to submit data:', error);
  }
};

const createLead = async (
  {
    user,
    page
  }: {
    user: { name: string; email: string };
    page: { url: string; title: string };
  },
  callback: () => void
) => {
  try {
    await fetch(
      'https://api.hsforms.com/submissions/v3/integration/submit/44326808/ea48c3f8-2102-4714-a83a-d4ba407fa66b',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          submittedAt: Date.now(),
          fields: [
            {
              objectTypeId: '0-1',
              name: 'email',
              value: user.email
            },
            {
              objectTypeId: '0-1',
              name: 'firstname',
              value: user.name
            }
          ],
          context: {
            ...(getCookie('hubspotutk') && {
              hutk: getCookie('hubspotutk')
            }),
            pageUri: page.url,
            pageName: page.title
          }
        })
      }
    );

    callback?.();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to create lead:', error);
  }
};

const Cover = ({
  share,
  onNext
}: {
  share: ShareProps;
  onNext: () => void;
}) => (
  <Stack
    css={{ w: '$full', '@md': { maxW: 537 }, h: '$full', gap: '$2dot5' }}
    flow="column"
  >
    <Text
      css={{
        fontStyle: 'italic',
        textTransform: 'uppercase',
        fontFamily: "'Inter', sans-serif",
        color: '#475467'
      }}
      variant="md"
      weight="bold"
    >
      Desafios Qconcursos
    </Text>

    <Text
      css={{ color: '#475467', fontFamily: "'Inter', sans-serif" }}
      variant="displayMd"
      weight="semibold"
    >
      Pronto para testar suas habilidades nesse concurso?
    </Text>

    <Text
      css={{ color: '#475467', fontFamily: "'Inter', sans-serif" }}
      variant="md"
    >
      Responda algumas questões rápidas e teste seus conhecimentos!
    </Text>

    <Stack
      css={{ gap: '$5', mt: '$2dot5' }}
      flow={{ '@initial': 'column', '@md': 'row' }}
    >
      <Button
        css={{
          w: '$full',
          '@md': { maxW: 240 },
          flexCenter: true,
          bgColor: '#FE6112',
          borderColor: '#FE6112',
          fontFamily: "'Inter', sans-serif",
          fontWeight: '$semibold',
          '&:hover, &:active, &:focus': {
            bgColor: '#FE7F40 !important',
            borderColor: '#FE7F40 !important'
          }
        }}
        rightIcon={<Icon icon={faArrowRight} />}
        size="xl"
        variant="secondary"
        onClick={onNext}
      >
        Iniciar teste
      </Button>
      <Button
        css={{
          d: 'flex',
          '@md': {
            d: 'none'
          },
          color: '#475467',
          textDecoration: 'solid underline #475467 2px'
        }}
        size="xl"
        variant="neutralLink"
        onClick={() => {
          if (navigator?.share) {
            navigator.share(share).then(() => {});
          }
        }}
      >
        Salvar para mais tarde
      </Button>
    </Stack>
  </Stack>
);

const Progress = ({
  current,
  total,
  value
}: {
  current: number;
  total: number;
  value: number;
}) => (
  <Stack css={{ flexCenter: true, gap: '$2', mx: 'auto', my: 0 }} flow="row">
    <ProgressBar
      css={{
        minMaxW: 270,
        '@md': { minMaxW: 440 },
        span: { bg: '$white', div: { bg: '#FE7F40' } }
      }}
      value={value}
    />
    <Text css={{ color: '#475467' }} weight="bold">
      {`${current}/${total}`}
    </Text>
  </Stack>
);

const Modal = ({
  onClick,
  onClose,
  isLoading
}: {
  onClick: (user: any) => void;
  onClose: () => void;
  isLoading: boolean;
}) => {
  const [user, setUser] = useState({});

  return (
    <Stack
      css={{
        w: '$full',
        maxW: 530,
        bgColor: '$white',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        rounded: '$4xl',
        boxShadow:
          '0px 20px 33px rgba(0, 0, 0, 0.03), 0px 8px 5px rgba(0, 0, 0, 0.08)'
      }}
      flow="column"
    >
      <Stack css={{ p: '$10', gap: '$5' }} flow="column">
        <Stack css={{ gap: '$2dot5', flexCenter: true }} flow="column">
          <Text
            css={{
              textAlign: 'center',
              fontFamily: "'Inter', sans-serif",
              color: '#FE6112'
            }}
            variant="xl"
            weight="bold"
          >
            Você está quase lá!
          </Text>
          <Text
            css={{
              textAlign: 'center',
              fontFamily: "'Inter', sans-serif",
              color: '#6B7280'
            }}
            variant="md"
          >
            Preencha seus dados e desbloqueie sua pontuação!
          </Text>
        </Stack>

        <Stack
          css={{ w: '$full', maxW: 400, mx: 'auto', gap: '$5' }}
          flow="column"
        >
          <Input
            css={{
              '> span': {
                fontFamily: "'Inter', sans-serif",
                fontWeight: '$semibold'
              },
              input: {
                color: '#475467',
                '&::placeholder': {
                  opacity: 1,
                  color: '#475467'
                }
              }
            }}
            label="Seu nome"
            placeholder="Seu nome"
            onBlur={e =>
              setUser(oldUser => ({
                ...oldUser,
                ...(e.target.value !== '' && { name: e.target.value })
              }))
            }
          />
          <Input
            css={{
              '> span': {
                fontFamily: "'Inter', sans-serif",
                fontWeight: '$semibold'
              },
              input: {
                color: '#475467',
                '&::placeholder': {
                  opacity: 1,
                  color: '#475467'
                }
              }
            }}
            label="Seu e-mail"
            placeholder="seunome@email.com"
            type="email"
            onBlur={e =>
              setUser(oldUser => ({
                ...oldUser,
                ...(e.target.value !== '' && { email: e.target.value })
              }))
            }
          />
        </Stack>
      </Stack>
      <Stack
        css={{
          py: '$3dot5',
          px: '$10',
          gap: '$2dot5',
          justifyContent: 'flex-end',
          bgColor: '$neutral50',
          borderTop: '1px solid $neutral200',
          borderBottomLeftRadius: '$4xl',
          borderBottomRightRadius: '$4xl'
        }}
        flow={{ '@initial': 'column', '@md': 'row' }}
      >
        <Button
          css={{
            color: '#334155',
            fontWeight: '$semibold',
            fontFamily: "'Inter', sans-serif"
          }}
          variant="neutralLink"
          onClick={onClose}
        >
          Não quero meu resultado
        </Button>
        <Button
          css={{
            fontWeight: '$semibold',
            fontFamily: "'Inter', sans-serif",
            bgColor: '#0694A2',
            borderColor: '#0694A2',
            '&:hover, &:active, &:focus': {
              bgColor: '#047481 !important',
              borderColor: '#047481 !important'
            },
            '&:disabled': {
              bgColor: '#047481 !important',
              borderColor: '#047481 !important'
            },
            justifyContent: 'center'
          }}
          disabled={Object.keys(user).length < 2}
          loading={isLoading}
          rightIcon={<Icon icon={faChevronRight} />}
          variant="primary"
          onClick={() => onClick(user)}
        >
          Ver meu resultado
        </Button>
      </Stack>
    </Stack>
  );
};

const Question = ({
  id,
  statement,
  alternatives = [],
  discipline_name,
  step,
  steps,
  isNext,
  isPrev,
  isFinish,
  isLoading,
  onNext,
  onPrev,
  onFinish,
  onSelect,
  page
}: {
  step: number;
  steps: number;
  isNext: boolean;
  isPrev: boolean;
  isFinish: boolean;
  isLoading: any;
  onNext: () => void;
  onPrev: () => void;
  onFinish: () => void;
  onSelect: (value: string) => void;
  page: ShareProps;
} & QuestionProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [answer, setAnswer] = useState<string | null>(null);

  const buildAlternatives = ['A', 'B', 'C', 'D', 'E'];

  return (
    <Stack css={{ gap: '$5', position: 'relative', minH: 600 }} flow="column">
      <Progress
        current={step}
        total={steps - 1}
        value={(step / (steps - 1)) * 100}
      />

      <Stack
        css={{
          bg: '$white',
          h: '$full',
          shadow: '$xs',
          rounded: '$lg',
          border: '1px solid $neutral200',
          pb: '$4',
          overflowY: 'auto',
          overflowX: 'hidden',
          maxH: 600
        }}
        flow="column"
      >
        <Stack
          css={{
            h: 65,
            p: '$4',
            borderBottom: '1px solid $neutral200',
            flexRowCenter: true,
            gap: '$1dot5',
            w: '$full',
            maxW: 340,
            '@md': {
              maxW: '100%'
            }
          }}
          flow="row"
        >
          <Stack
            css={{
              border: '1px solid $neutral200',
              bg: '$neutral100',
              rounded: '$md',
              flexCenter: true,
              px: '$2',
              fontSize: '$sm',
              color: '#1F2937',
              mr: '$2',
              fontFamily: "'Inter', sans-serif"
            }}
            flow="row"
          >
            {id}
          </Stack>

          {[discipline_name].map((subject, idx) => (
            <Fragment key={subject}>
              {idx > 0 && (
                <Icon css={{ color: '$neutral300' }} icon={faCaretRight} />
              )}
              <Text
                css={{
                  fontFamily: "'Inter', sans-serif",
                  ...(idx !== 0 && {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: '#475467'
                  })
                }}
                variant="sm"
                weight="semibold"
              >
                {subject}
              </Text>
            </Fragment>
          ))}
        </Stack>
        <Box
          css={{
            fontFamily: "'Inter', sans-serif",
            color: '#475467',
            p: '$4',
            fontSize: '$md'
          }}
          dangerouslySetInnerHTML={{ __html: statement }}
        />
        <Box css={{ px: '$4', pt: '$2' }}>
          <Stack css={{ gap: '$3' }} flow="column">
            {(alternatives?.length === 0
              ? [
                  { item: 'C', statement: 'Certo' },
                  { item: 'E', statement: 'Errado' }
                ]
              : alternatives.map((alternative, idx) => ({
                  item: buildAlternatives[idx],
                  statement: alternative
                }))
            ).map((alternative, idx) => (
              <Label
                key={`option_${idx}`}
                as="label"
                css={{
                  display: 'flex',
                  flexRowCenter: true,
                  rounded: '$full',
                  minH: '$12',
                  px: '$2',
                  gap: '$4',
                  cursor: 'pointer'
                }}
              >
                <Box
                  as="input"
                  name="answer[]"
                  css={{
                    display: 'none',
                    '&:checked + span': {
                      backgroundColor: '#FE6112',
                      color: '$white'
                    }
                  }}
                  type="radio"
                  value={alternative.item}
                  onChange={() => {
                    setAnswer(alternative.item);
                    onSelect(alternative.item);
                  }}
                />
                <Text
                  css={{
                    w: '$8',
                    h: '$8',
                    display: 'flex',
                    flexCenter: true,
                    border: '1px solid #FE6112',
                    rounded: '$full',
                    color: '#FE6112',
                    fontFamily: "'Inter', sans-serif"
                  }}
                  weight="bold"
                >
                  {alternative.item}
                </Text>
                <Box
                  css={{
                    fontFamily: "'Inter', sans-serif",
                    color: '#252F3F',
                    fontSize: '$md',
                    flex: 1
                  }}
                  dangerouslySetInnerHTML={{ __html: alternative.statement }}
                />
              </Label>
            ))}
          </Stack>
        </Box>
      </Stack>

      <Stack
        css={{ justifyContent: 'center', gap: '$2', mt: 'auto' }}
        flow="row"
      >
        <Button
          css={{ fontFamily: "'Inter', sans-serif", fontWeight: '$semibold' }}
          disabled={!isPrev || isLoading}
          leftIcon={<Icon icon={faArrowLeft} />}
          size="lg"
          variant="neutral"
          onClick={() => {
            setAnswer(null);
            onPrev();
          }}
        >
          Anterior
        </Button>
        {isNext && !isFinish && (
          <Button
            css={{ fontFamily: "'Inter', sans-serif", fontWeight: '$semibold' }}
            disabled={!answer || isLoading}
            rightIcon={<Icon icon={faArrowRight} />}
            size="lg"
            variant="neutral"
            onClick={onNext}
          >
            Próxima
          </Button>
        )}
        {isFinish && (
          <Button
            css={{ fontFamily: "'Inter', sans-serif", fontWeight: '$semibold' }}
            disabled={!answer}
            loading={isLoading}
            rightIcon={<Icon icon={faArrowRight} />}
            size="lg"
            variant="neutral"
            onClick={
              (window as any).__userSession
                ? () => {
                    setLoading(true);
                    createLead(
                      { user: (window as any).__userSession, page },
                      () => {
                        setLoading(false);
                        onFinish();
                      }
                    );
                  }
                : () => setOpen(true)
            }
          >
            Ver Resultado
          </Button>
        )}
      </Stack>

      {open && (
        <Box
          css={{
            w: 'calc($full + $12)',
            h: 'calc($full + $12)',
            bgColor: '#1F2937B2',
            position: 'absolute',
            top: '-$6',
            left: '-$6'
          }}
        >
          <Modal
            isLoading={loading}
            onClick={user => {
              setLoading(true);
              createLead({ user, page }, () => {
                setLoading(false);
                onFinish();
              });
            }}
            onClose={() => setOpen(false)}
          />
        </Box>
      )}
    </Stack>
  );
};

const CardCourse = ({
  course_slug,
  opportunity_job_name,
  institute_initials,
  institute_image,
  institute_name,
  contest_year,
  salary
}: CourseProps) => (
  <Stack
    as="a"
    css={{
      w: '$full',
      bg: '$white',
      rounded: '$4xl',
      border: '1px solid $neutral200',
      p: '$4',
      gap: '$2dot5',
      alignItems: 'flex-start',
      '&:hover, &:active, &:focus': {
        borderColor: '#FE6112'
      },
      '&:active, &:focus': {
        outlineOffset: 0,
        outlineWidth: 3,
        outlineColor: '#FFE6D5',
        outlineStyle: 'solid'
      },
      zIndex: 2,
      position: 'relative',
      '@md': {
        maxW: 340
      }
    }}
    flow="column"
    href={`${ODIN_PLATFORM_URL}/cursos/${course_slug}`}
  >
    <Text
      css={{
        fontFamily: "'Inter', sans-serif",
        color: '#475467',
        textAlign: 'left'
      }}
      variant="md"
      weight="semibold"
    >
      {institute_initials} - {opportunity_job_name} - {contest_year}
    </Text>

    <Stack css={{ w: '$full', gap: '$1dot5', justifyContent: 'flex-start' }}>
      {[
        `${institute_initials} - ${contest_year}`,
        `Salário ${currencyFormatter.format(salary / 100)}`
      ]
        .filter(Boolean)
        .map(item => (
          <Badge
            key={item}
            css={{
              bgColor: '$neutral50',
              color: '#0B4A6F',
              fontFamily: "'Inter', sans-serif",
              fontWeight: '$semibold'
            }}
          >
            {item}
          </Badge>
        ))}
    </Stack>

    <Stack
      css={{
        w: '$full',
        justifyContent: 'space-between',
        pt: '$2dot5',
        borderTop: '1px solid $neutral200'
      }}
      flow="row"
    >
      <Stack css={{ justifyContent: 'space-between' }} flow="column">
        <Text
          css={{ fontFamily: "'Inter', sans-serif", color: '#475467' }}
          variant="displaySm"
          weight="bold"
        >
          Grátis
        </Text>
        <Text
          css={{ fontFamily: "'Inter', sans-serif", color: '#475467' }}
          variant="md"
        >
          para começar a estudar
        </Text>
      </Stack>
      <Stack
        css={{
          flexCenter: true,
          w: '$16',
          h: '$16',
          bgColor: '$neutral50',
          rounded: '$full'
        }}
      >
        <img
          width={32}
          height={32}
          alt={`Logo ${institute_name}`}
          src={institute_image || ''}
        />
      </Stack>
    </Stack>
  </Stack>
);

const ProgressCircle = ({
  value = 0,
  style = 'too_bad'
}: {
  value: number;
  style: string;
}) => {
  const radius = 190;
  const strokeWidth = radius * 0.14;
  const innerRadius = radius - strokeWidth / 2;

  const circumference = innerRadius * 2 * Math.PI;
  const arc = circumference * (180 / 360);
  const dashArray = `${arc} ${circumference}`;
  const transform = `rotate(180, ${radius}, ${radius})`;

  const offset = arc - (value / 100) * arc;

  const styles: Record<string, Record<string, string>> = {
    too_bad: {
      bgColor: '#CBD5E1',
      color: '#475569'
    },
    bad: {
      bgColor: '#FDE68A',
      color: '#FBBF24'
    },
    average: {
      bgColor: '#7DD3FC',
      color: '#0EA5E9'
    },
    good: {
      bgColor: '#FFCCB2',
      color: '#FE6112'
    }
  };

  return (
    <Box
      css={{
        h: 160,
        position: 'relative',
        '@md': {
          h: radius
        },
        svg: {
          width: radius * 1.5,
          height: radius * 1.5,
          '@md': { width: radius * 2, height: radius * 2 }
        }
      }}
    >
      <Box
        css={{
          w: 190,
          bgColor: '#111827',
          h: '$10',
          rounded: '$lg',
          d: 'flex',
          flexCenter: true,
          mx: 'auto',
          position: 'absolute',
          top: '-$10',
          left: '50%',
          transform: 'translateX(-50%)',
          '&:before': {
            bgColor: '#111827',
            w: '$6',
            h: '$6',
            content: "''",
            position: 'absolute',
            top: 'calc($full - $4)',
            rounded: '$md',
            zIndex: -1,
            transform: 'rotate(45deg)'
          }
        }}
      >
        <Text
          css={{
            color: '$white',
            fontFamily: "'Inter', sans-serif"
          }}
          variant="lg"
        >
          {value === 0 ? 'Você não acertou' : `Você acertou: ${value}%`}
        </Text>
      </Box>
      <svg viewBox="0 0 380 380">
        <circle
          cx={radius}
          cy={radius}
          fill="transparent"
          r={innerRadius}
          stroke={styles[style].bgColor}
          strokeDasharray={dashArray}
          strokeWidth={strokeWidth}
          transform={transform}
        />
        <circle
          style={{
            transition: 'stroke-dashoffset 0.3s',
            border: '2px solid black'
          }}
          cx={radius}
          cy={radius}
          fill="transparent"
          r={innerRadius}
          stroke={styles[style].color}
          strokeDasharray={dashArray}
          strokeDashoffset={offset}
          strokeWidth={strokeWidth}
          transform={transform}
        />
      </svg>
    </Box>
  );
};

const Animation = () => (
  <Box
    css={{
      position: 'absolute',
      top: -24,
      left: -60,
      w: 'calc($full + 120px)',
      h: 260,
      overflow: 'hidden',
      zIndex: 0
    }}
  >
    <Confetti width={700} height={260} numberOfPieces={50} />
  </Box>
);

const Result = ({
  data,
  onReset,
  course
}: {
  data: any;
  onReset: () => void;
  course: CourseProps;
}) => {
  const title: Record<string, string> = {
    too_bad: 'Não desista!',
    bad: 'Não desista!',
    average: 'Parabéns!',
    good: 'Parabéns!'
  };

  const text: Record<string, string> = {
    too_bad:
      'A jornada até a aprovação pode ficar mais fácil com a ajuda de especialistas, mantenha o foco!',
    bad: 'A jornada até a aprovação pode ficar mais fácil com a ajuda de especialistas, mantenha o foco!',
    average:
      'Seus esforços estão valendo a pena, mas é preciso manter o foco para chegar ainda mais longe!',
    good: 'Você ficou entre os melhores, continue estudando e fique cada vez mais perto da aprovação!'
  };

  return (
    <Stack
      css={{
        w: '$full',
        maxW: 600,
        position: 'relative',
        mx: 'auto',
        pt: '$20',
        flexCenter: true,
        gap: '$5'
      }}
      flow="column"
    >
      {data.percentage > 50 && <Animation />}
      <ProgressCircle style={data.category} value={data.percentage} />
      <Stack
        css={{
          gap: '$5',
          flexCenter: true,
          mt: '-$20',
          mb: '$3',
          '@md': { mb: '$5' }
        }}
        flow="column"
      >
        <Text
          css={{
            color: '#475467',
            textAlign: 'center',
            fontFamily: "'Inter', sans-serif"
          }}
          variant={{ '@initial': 'displaySm', '@md': 'displayMd' }}
          weight="bold"
        >
          {title[data.category as string]}
        </Text>
        <Text
          css={{
            color: '#475467',
            textAlign: 'center',
            fontFamily: "'Inter', sans-serif"
          }}
          variant="xl"
        >
          {text[data.category as string]}
        </Text>
      </Stack>

      <Stack
        css={{
          w: '$full',
          gap: '$2dot5',
          justifyContent: 'space-between',
          mb: '$3',
          '@md': { flexRowCenter: true, mb: '$5' }
        }}
        flow={{ '@initial': 'column', '@md': 'row' }}
      >
        <Stack
          css={{
            w: '$full',
            flex: 1,
            bt: '1px solid rgba(71, 84, 103, 0.28)',
            pt: '$3',
            '@md': { maxW: 270, bt: 'none', pt: 0 },
            gap: '$2dot5'
          }}
          flow="column"
        >
          <Text
            css={{
              color: '#475467',
              textAlign: 'center',
              fontFamily: "'Inter', sans-serif",
              '@md': { textAlign: 'left' }
            }}
            variant={{ '@initial': 'xl', '@md': 'displayXs' }}
          >
            Comece a{' '}
            <Text
              css={{ fontFamily: "'Inter', sans-serif" }}
              variant={{ '@initial': 'xl', '@md': 'displayXs' }}
              weight="bold"
            >
              estudar de graça
            </Text>{' '}
            hoje mesmo com o Qconcursos:
          </Text>
        </Stack>
        <Box css={{ flex: 1 }}>
          <CardCourse {...course} />
        </Box>
      </Stack>

      <Stack
        css={{
          w: '$full',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '$2'
        }}
        flow="row"
      >
        <Button
          css={{
            borderColor: '#6D7E9733',
            fontFamily: "'Inter', sans-serif",
            color: '#6D7E97',
            pr: '$4',
            fontWeight: '$semibold'
          }}
          rightIcon={<Icon icon={faSync} />}
          variant="neutral"
          onClick={onReset}
        >
          Repetir teste
        </Button>

        <Stack
          css={{
            gap: '$2dot5',
            flexCenter: true
          }}
          flow="row"
        >
          <Text
            css={{ color: '#64748B', fontFamily: "'Inter', sans-serif" }}
            variant="xs"
            weight="semibold"
          >
            Gabarito
          </Text>

          <Stack css={{ gap: '$1', flexCenter: true }} flow="row">
            {data.answerSheet.map((answer: string, idx: number) => (
              <Text
                key={`answer_${idx}`}
                css={{
                  shadow: '$xs',
                  bg: '$white',
                  color: '#64748B',
                  w: '$6',
                  h: '$6',
                  rounded: '$full',
                  display: 'flex',
                  flexCenter: true,
                  fontFamily: "'Inter', sans-serif"
                }}
                variant="xs"
                weight="semibold"
              >
                {answer}
              </Text>
            ))}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

const styles: Record<string, string> = {
  too_bad:
    'linear-gradient(180deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0) 52%), #F1F5F9;',
  bad: 'linear-gradient(180deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0) 52%), #FFFBEB;',
  average:
    'linear-gradient(180deg, rgba(255, 255, 255, 0.6) 0%, rgba(255, 255, 255, 0) 52%), #F0F9FF;',
  good: 'linear-gradient(180deg, rgba(255, 255, 255, 0.6) 0%, rgba(247, 244, 240, 0) 52%), #FFF4ED;',
  default:
    'linear-gradient(0deg, rgba(255, 179, 140, 0.2) 0%, rgba(255, 183, 148, 0) 100%), linear-gradient(180deg, rgba(255, 230, 213, 0.4) 0%, rgba(247, 244, 240, 0) 52%), #F7F4F0'
};

const Questions = ({
  questions,
  course,
  // user,
  share: { url, title }
}: {
  questions: QuestionProps[];
  course: CourseProps;
  // user: AtreusUser | null;
  share: ShareProps;
}) => {
  const data = questions;
  const [answers, setAnswers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);

  const handleReset = () => {
    setStep(0);
    setResult(null);
    setAnswers([]);
  };

  const handleNext = () => {
    setStep(step + 1);
  };

  const handlePrev = () => {
    setStep(step - 1);
  };

  const totalSteps = data.length + 1;

  const steps = [
    <Cover key="step_0" share={{ url, title }} onNext={() => handleNext()} />,
    ...data.map((question, idx) => (
      <Question
        key={`step_${idx + 1}`}
        {...question}
        isFinish={step === data.length}
        isLoading={loading}
        isNext={step < totalSteps}
        isPrev={step > 1}
        page={{ url, title }}
        step={step}
        steps={totalSteps}
        // user={(window as any).__userSession}
        onFinish={() => {
          setLoading(true);
          submit({ questions: data, answers }, results => {
            setLoading(false);
            setResult(results);
            handleNext();
          });
        }}
        onNext={() => handleNext()}
        onPrev={() => handlePrev()}
        onSelect={answer => setAnswers(oldAnswers => [...oldAnswers, answer])}
      />
    )),
    <Result
      key={`step_${totalSteps}`}
      course={course}
      data={result}
      onReset={() => handleReset()}
    />
  ];

  return (
    <Box
      css={{
        w: '$full',
        p: '$6',
        bg: result
          ? styles[(result as { category: string }).category]
          : styles.default,
        minH: 274
      }}
    >
      {steps[step]}
    </Box>
  );
};

export default Questions;
